/**
 * Product Page - Dynamic Price Update & Buy Now Functionality
 * Updates the Add to Cart button price based on bundle/variant selection
 */

(function() {
  'use strict';

  // Configuration
  const CONFIG = {
    selectors: {
      addToCartBtn: '[data-add-to-cart]',
      buyNowBtn: '[data-buy-now]',
      priceDisplay: '[data-bundle-price]',
      productForm: '.f-product-form form',
      variantInput: 'input[name="id"]',
      quantityInput: '[name="quantity"]',
      bundleContainer: '#fox-product-bundle-inside'
    }
  };

  class ProductButtonManager {
    constructor() {
      this.addToCartBtn = document.querySelector(CONFIG.selectors.addToCartBtn);
      this.buyNowBtn = document.querySelector(CONFIG.selectors.buyNowBtn);
      this.priceDisplay = document.querySelector(CONFIG.selectors.priceDisplay);
      this.productForm = document.querySelector(CONFIG.selectors.productForm);
      this.variantInput = document.querySelector(CONFIG.selectors.variantInput);
      this.quantityInput = document.querySelector(CONFIG.selectors.quantityInput);
      
      if (this.addToCartBtn && this.productForm) {
        this.init();
      }
    }

    init() {
      this.setupEventListeners();
      this.setupBundleObserver();
      this.updatePrice();
    }

    setupEventListeners() {
      // Variant change listener
      if (this.variantInput) {
        const variantObserver = new MutationObserver(() => this.updatePrice());
        variantObserver.observe(this.variantInput, {
          attributes: true,
          attributeFilter: ['value']
        });
      }

      // Quantity change listener
      if (this.quantityInput) {
        this.quantityInput.addEventListener('change', () => this.updatePrice());
      }

      // Buy Now button handler
      if (this.buyNowBtn) {
        this.buyNowBtn.addEventListener('click', (e) => this.handleBuyNow(e));
      }

      // Listen for custom variant change events (if your theme uses them)
      document.addEventListener('variant:change', () => this.updatePrice());
    }

    setupBundleObserver() {
      // Watch for Fox Kit bundle changes
      const bundleContainer = document.querySelector(CONFIG.selectors.bundleContainer);
      if (bundleContainer) {
        const bundleObserver = new MutationObserver(() => {
          setTimeout(() => this.updatePrice(), 300);
        });
        bundleObserver.observe(bundleContainer, {
          childList: true,
          subtree: true,
          attributes: true
        });

        // Listen for checkbox changes in bundle
        bundleContainer.addEventListener('change', () => {
          setTimeout(() => this.updatePrice(), 100);
        });
      }
    }

    updatePrice() {
      const totalPrice = this.calculateTotalPrice();
      if (this.priceDisplay && totalPrice) {
        this.priceDisplay.textContent = this.formatMoney(totalPrice);
      }
    }

    calculateTotalPrice() {
      let totalPrice = 0;

      // Get base variant price
      const basePrice = this.getVariantPrice();
      const quantity = this.getQuantity();
      
      if (basePrice) {
        totalPrice = basePrice * quantity;
      }

      // Add bundle items price if Fox Kit bundle exists
      const bundlePrice = this.getBundlePrice();
      if (bundlePrice > 0) {
        totalPrice += bundlePrice * quantity;
      }

      return totalPrice;
    }

    getVariantPrice() {
      if (this.addToCartBtn) {
        const price = this.addToCartBtn.dataset.variantPrice;
        return price ? parseInt(price, 10) : 0;
      }
      return 0;
    }

    getQuantity() {
      if (this.quantityInput) {
        return parseInt(this.quantityInput.value, 10) || 1;
      }
      return 1;
    }

    getBundlePrice() {
      let bundlePrice = 0;
      
      // Check for Fox Kit bundle checkboxes
      const bundleCheckboxes = document.querySelectorAll('#fox-product-bundle-inside input[type="checkbox"]:checked');
      
      bundleCheckboxes.forEach(checkbox => {
        const priceElement = checkbox.closest('.product-bundle-item')?.querySelector('[data-price]');
        if (priceElement) {
          const price = priceElement.dataset.price;
          bundlePrice += parseInt(price, 10) || 0;
        }
      });

      return bundlePrice;
    }

    formatMoney(cents) {
      // Basic money formatting - adjust based on your shop's currency settings
      const dollars = (cents / 100).toFixed(2);
      
      // Get currency symbol from Shopify if available
      if (typeof Shopify !== 'undefined' && Shopify.currency && Shopify.currency.active) {
        return Shopify.formatMoney(cents);
      }
      
      // Fallback formatting
      return `$${dollars}`;
    }

    async handleBuyNow(e) {
      e.preventDefault();
      
      if (!this.productForm) return;

      // Disable buttons during processing
      this.setButtonsLoading(true);

      try {
        // Get form data
        const formData = new FormData(this.productForm);
        
        // Add to cart
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          body: formData
        });

        if (!response.ok) {
          throw new Error('Failed to add to cart');
        }

        // Redirect to checkout
        window.location.href = '/checkout';
        
      } catch (error) {
        console.error('Buy Now error:', error);
        alert('Sorry, there was an error processing your request. Please try again.');
        this.setButtonsLoading(false);
      }
    }

    setButtonsLoading(loading) {
      if (this.addToCartBtn) {
        this.addToCartBtn.disabled = loading;
      }
      if (this.buyNowBtn) {
        this.buyNowBtn.disabled = loading;
        this.buyNowBtn.textContent = loading ? 'Processing...' : 'Buy Now – Skip Cart';
      }
    }
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      new ProductButtonManager();
    });
  } else {
    new ProductButtonManager();
  }

  // Re-initialize on section reload (for Shopify theme editor)
  document.addEventListener('shopify:section:load', () => {
    new ProductButtonManager();
  });

})();